import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://kakobuypolska.com";

export default function BlogArticlePage() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    const els = contentRef.current?.querySelectorAll(".reveal-content");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Kakobuy Spreadsheet Polska — Kompletny przewodnik 2026",
    description:
      "Jeśli szukasz najlepszych produktów z Chin i chcesz zaoszczędzić godziny na przeszukiwaniu setek stron, Kakobuy Spreadsheet Polska jest narzędziem, którego potrzebujesz.",
    image:
      "https://readdy.ai/api/search-image?query=Flat%20lay%20fashion%20shopping%20spreadsheet%20with%20sneakers%20hoodies%20and%20accessories%20on%20clean%20minimal%20cream%20desk%20warm%20golden%20lighting%20editorial%20photography%20high%20detail%20organized%20composition&width=1200&height=600&seq=static-article-v2-hero&orientation=landscape&nocache=true",
    datePublished: "2026-07-16",
    dateModified: "2026-07-20",
    author: {
      "@type": "Organization",
      name: "Kakobuy Spreadsheet Polska",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Kakobuy Spreadsheet Polska",
      logo: {
        "@type": "ImageObject",
        url: "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/kakobuy-spreadsheet-kompletny-przewodnik`,
    },
  };

  return (
    <div className="min-h-[100dvh] flex flex-col">
      <JsonLd data={articleJsonLd} />
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Article Header */}
        <section className="relative">
          <div className="w-full h-[300px] md:h-[420px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Flat%20lay%20fashion%20shopping%20spreadsheet%20with%20sneakers%20hoodies%20and%20accessories%20on%20clean%20minimal%20cream%20desk%20warm%20golden%20lighting%20editorial%20photography%20high%20detail%20organized%20composition&width=1200&height=600&seq=static-article-v2-hero&orientation=landscape&nocache=true"
              alt="Kakobuy Spreadsheet Polska"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-foreground-950/20" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-8 lg:px-12 pb-8 md:pb-12">
            <div className="max-w-3xl mx-auto">
              <span className="inline-block text-xs font-medium text-primary-300 bg-primary-500/20 px-3 py-1 rounded-full mb-3">
                Przewodnik
              </span>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 leading-tight">
                Kakobuy Spreadsheet Polska — Kompletny przewodnik 2026
              </h1>
              <div className="flex items-center gap-4 mt-4 text-sm text-background-300/80">
                <span>16 lipca 2026</span>
                <span className="w-1 h-1 rounded-full bg-background-400" />
                <span>8 min czytania</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section ref={contentRef} className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Intro */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500">
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                Jeśli szukasz najlepszych produktów z Chin i chcesz zaoszczędzić godziny na przeszukiwaniu setek stron,
                <strong> Kakobuy Spreadsheet Polska</strong> jest narzędziem, którego potrzebujesz.
                W tym kompletnym przewodniku wyjaśnimy wszystko — od tego, czym jest Kakobuy Spreadsheet,
                przez to jak z niego korzystać, aż po wskazówki dotyczące bezpieczeństwa zakupów.
              </p>
            </div>

            {/* What is Kakobuy */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-100">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10">
                Czym jest Kakobuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                <strong>Kakobuy Spreadsheet</strong> to zorganizowana baza danych produktów dostępnych
                na platformie Kakobuy — jednym z najpopularniejszych agentów zakupowych z Chin.
                Zamiast przeszukiwać dziesiątki stron na Taobao, Weidian czy 1688, wszystkie najlepsze
                znaleziska są posegregowane według kategorii w jednym miejscu.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Każdy produkt w bazie zawiera gotowy link do zakupu, zdjęcia kontroli jakości (QC),
                informacje o cenie i oceny od innych użytkowników. To narzędzie stworzone przez
                społeczność dla społeczności — każdego dnia pojawiają się nowe znaleziska i aktualizacje.
              </p>
            </div>

            {/* Why use */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-150">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10">
                Dlaczego warto korzystać z Kakobuy Spreadsheet?
              </h2>
              <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/60 mb-6">
                <ul className="flex flex-col gap-3">
                  {[
                    "Tysiące gotowych linków — nie musisz szukać produktów ręcznie",
                    "Posegregowane kategorie — odzież, obuwie, akcesoria, perfumy i więcej",
                    "Zdjęcia QC — sprawdź jakość produktu przed zakupem",
                    "Aktualizowane codziennie — nowe produkty i linki na bieżąco",
                    "Oszczędność czasu — znajdź to, czego szukasz w kilka minut",
                    "Społeczność użytkowników — opinie i rekomendacje od innych",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-50 text-accent-600 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* How to use */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-200">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10">
                Jak korzystać z Kakobuy Spreadsheet — krok po kroku
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Korzystanie z Kakobuy Spreadsheet jest proste, nawet jeśli jesteś zupełnym nowicjuszem.
                Oto cztery kroki, które pozwolą Ci zacząć:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {[
                  { step: "Krok 1", title: "Wybierz kategorię", desc: "Przeglądaj kategorie produktów — od sneakersów po streetwear i akcesoria." },
                  { step: "Krok 2", title: "Znajdź produkt", desc: "Przeglądaj gotowe linki i znajdź produkt, który Cię interesuje." },
                  { step: "Krok 3", title: "Sprawdź QC", desc: "Zobacz zdjęcia kontroli jakości i opinie innych kupujących." },
                  { step: "Krok 4", title: "Kup produkt", desc: "Kliknij link i kup bezpośrednio przez Kakobuy. Szybko i bezpiecznie." },
                ].map((s) => (
                  <div key={s.step} className="bg-background-100 rounded-xl p-4 md:p-5 border border-background-200/60">
                    <span className="text-xs font-semibold text-primary-500 uppercase tracking-wide">{s.step}</span>
                    <h3 className="text-base font-semibold text-foreground-950 mt-1 mb-1">{s.title}</h3>
                    <p className="text-sm text-foreground-600">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Safety */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-250">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10">
                Bezpieczeństwo zakupów na Kakobuy
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Jednym z najczęstszych pytań nowych użytkowników jest: czy Kakobuy jest bezpieczne?
                Odpowiedź brzmi: tak, pod warunkiem że przestrzegasz kilku podstawowych zasad.
              </p>
              <div className="bg-accent-50/50 rounded-xl p-5 md:p-6 border border-accent-200/40 mb-6">
                <h3 className="text-base font-semibold text-accent-800 mb-3">
                  Zasady bezpiecznych zakupów:
                </h3>
                <ul className="flex flex-col gap-2.5">
                  {[
                    "Zawsze sprawdzaj ratingi sprzedawcy przed zakupem",
                    "Żądaj zdjęć QC przed zatwierdzeniem wysyłki",
                    "Unikaj produktów bez opinii lub z niską oceną",
                    "Korzystaj z oficjalnych linków z Kakobuy Spreadsheet",
                    "Nie kupuj od niezweryfikowanych źródeł poza platformą",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <i className="ri-shield-check-line text-accent-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Shipping */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-300">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10">
                Wysyłka do Polski — ile to trwa i ile kosztuje?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Wysyłka z Chin do Polski zazwyczaj trwa od 10 do 25 dni roboczych, w zależności od wybranej
                metody dostawy. Najpopularniejsze opcje to:
              </p>
              <div className="overflow-x-auto rounded-xl border border-background-200 mb-6">
                <table className="w-full min-w-[400px]">
                  <thead>
                    <tr className="border-b border-background-200 bg-background-100">
                      <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-700">Metoda</th>
                      <th className="text-center px-4 py-3 text-sm font-semibold text-foreground-700">Czas</th>
                      <th className="text-center px-4 py-3 text-sm font-semibold text-foreground-700">Cena (orientacyjna)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { method: "DHL", time: "10-15 dni", price: "~80-150 zł" },
                      { method: "EMS", time: "12-18 dni", price: "~60-120 zł" },
                      { method: "EUB", time: "15-25 dni", price: "~40-80 zł" },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 1 ? "bg-background-50/50" : ""}>
                        <td className="px-4 py-3 text-sm text-foreground-800 font-medium">{row.method}</td>
                        <td className="px-4 py-3 text-sm text-foreground-600 text-center">{row.time}</td>
                        <td className="px-4 py-3 text-sm text-foreground-600 text-center">{row.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Categories deep dive */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-350">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4 mt-10">
                Najpopularniejsze kategorie w Kakobuy Spreadsheet
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Kakobuy Spreadsheet obejmuje ponad 14 kategorii produktów. Oto najpopularniejsze z nich,
                które przyciągają najwięcej użytkowników z Polski:
              </p>
              <div className="flex flex-col gap-3 mb-6">
                {[
                  { name: "Sneakersy i buty", desc: "Nike, Adidas, Jordan, New Balance — repliki premium i budżetowe." },
                  { name: "Streetwear i bluzy", desc: "Supreme, Palace, Chrome Hearts — wszystko co najmodniejsze." },
                  { name: "Kurtki i outerwear", desc: "North Face, Stone Island, Arc'teryx — na każdą pogodę." },
                  { name: "Akcesoria", desc: "Torby, paski, biżuteria, okulary — detale, które robią różnicę." },
                  { name: "Perfumy", desc: "Niche i designer fragrances — luksusowe zapachy w atrakcyjnych cenach." },
                ].map((cat, i) => (
                  <div key={i} className="flex items-start gap-3 bg-background-100 rounded-lg p-4 border border-background-200/60">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0 text-sm font-bold">
                      {i + 1}
                    </span>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-950">{cat.name}</h4>
                      <p className="text-xs text-foreground-600 mt-0.5">{cat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-400">
              <div className="bg-primary-500/5 rounded-xl p-6 md:p-8 border border-primary-200/30 text-center mt-10">
                <h3 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-3">
                  Gotowy na swoje pierwsze zakupy?
                </h3>
                <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">
                  Przejdź do pełnej bazy Kakobuy Spreadsheet i znajdź produkty, które pokochasz.
                  Tysiące gotowych linków czeka na Ciebie.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Kakobuy Spreadsheet
                    <i className="ri-external-link-line" />
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 bg-background-100 text-foreground-950 text-sm font-medium px-6 py-2.5 rounded-full border border-background-300 hover:bg-background-200 transition-colors whitespace-nowrap"
                  >
                    Buy it now
                    <i className="ri-shopping-bag-line" />
                  </a>
                </div>
              </div>
            </div>

            {/* Internal Links */}
            <div className="reveal-content opacity-0 translate-y-4 transition-all duration-500 delay-450">
              <div className="mt-10 pt-6 border-t border-background-200">
                <h3 className="text-sm font-semibold text-foreground-700 mb-3">
                  Przeczytaj również:
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Link
                    to="/"
                    className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors"
                  >
                    Strona główna
                  </Link>
                  <Link
                    to="/kakobuy-spreadsheet"
                    className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors"
                  >
                    Kakobuy Spreadsheet
                  </Link>
                  <Link
                    to="/kakobuy-finds"
                    className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors"
                  >
                    Najlepsze Finds
                  </Link>
                  <Link
                    to="/faq"
                    className="text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full hover:bg-primary-100 transition-colors"
                  >
                    FAQ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        .reveal-content.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
    </div>
  );
}